import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Broker } from '@c4/core';
import { Observable } from 'rxjs/Observable';
import { Injectable } from '@angular/core';

import { Event } from './../../../common/interfaces/event.interface';
import { ItemsService } from './../../../common/services/items.service';
import { ProgrammingUtilsService } from './../../../common/services/utils.service';
import { SharedProgrammingService } from '../../../common/services/shared-programming.service';
import { Device } from '../../../common/interfaces/item.interface';
import { DeviceEvent } from '../../../common/interfaces/event.interface';
import { discoveredDevice } from '../../../../../../sysman/src/app/services/discovery.service';

const IGNORABLE_BUTTON_ACTIONS: string[] = ['pushed', 'pressed', 'released'];

export interface KeypadProgrammingContext {
  ready: boolean;
  keypads?: Array<Device>;
  trigger: {
    keypad?: any;
    buttons?: Array<any>;
    button?: any;
    events?: Array<DeviceEvent>;
    event?: DeviceEvent;
  }
}

const INITIAL_STATE: KeypadProgrammingContext = {
  trigger: {},
  ready: false
}

const STORE_NAME: string = 'PROGRAMMING:QUICKSTARTS:KEYPADS:';
const CLEAR_CONTEXT: string = STORE_NAME + 'CLEAR_CONTEXT';
const SET_DEVICES: string = STORE_NAME + 'SET_DEVICES';
const SET_DEVICE: string = STORE_NAME + 'SET_DEVICE';
const SET_BUTTONS: string = STORE_NAME + 'SET_BUTTONS';
const SET_BUTTON: string = STORE_NAME + 'SET_BUTTON';
const SET_EVENTS: string = STORE_NAME + 'SET_EVENTS';
const CLEAR_BUTTONS: string = STORE_NAME + 'CLEAR_BUTTONS';
const SET_READY: string = STORE_NAME + 'SET_READY';

export function keypadProgrammingReducer(state: KeypadProgrammingContext = INITIAL_STATE, { type, payload }) {

  switch (type) {
    case CLEAR_CONTEXT: return Object.assign({}, INITIAL_STATE);
    case SET_DEVICES: return Object.assign({}, state, { keypads: payload });
    case SET_DEVICE: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { keypad: payload }) });
    case SET_BUTTONS: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { buttons: payload }) });
    case SET_BUTTON: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { button: payload }) });
    case SET_EVENTS: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { events: payload }) });
    case CLEAR_BUTTONS: return Object.assign({}, state, { trigger: Object.assign({}, state.trigger, { buttons: [] }) });
    case SET_READY: return Object.assign({}, state, { ready: payload });

    default: return state;
  }
};

@Injectable()
export class QuickstartKeypadsService {

  selected: boolean;
  trigger: Observable<any>;
  allKeypads: Observable<Device[]>;
  ready: Observable<boolean>;

  constructor(
    protected store: Store<{ keypadProgramming: KeypadProgrammingContext }>,
    protected broker: Broker,
    protected utils: ProgrammingUtilsService,
    protected shared: SharedProgrammingService,
    private items: ItemsService,
    private router: Router
  ) {
    this.allKeypads = <Observable<Device[]>>this.items.itemsList.map(items => {
      return items.filter((item: Device) => {
        return (item.type === 7 && item.proxy === 'keypad_proxy');
      });
    });

    this.ready = Observable.combineLatest(
      this.shared.isReady,
      this.store.select(s => s.keypadProgramming.ready),
      (a, b) => (a && b)
    );
  }

  setKeypad(keypad: Device) {
    this.store.dispatch({ type: SET_READY, payload: false });
    this.store.dispatch({ type: SET_DEVICE, payload: keypad });
    this.getEvents(keypad);
  }

  setButton(button: any) {
    this.store.dispatch({ type: SET_BUTTON, payload: button });
    this.setInteractions(button);
  }

  private isSupportedProxy(proxy: string): boolean {
    return (['keypad_proxy'].indexOf(proxy) >= 0);
  }

  private setInteractions(button: any) {
    console.log('setting interactions for button', button);
    let events = this.utils.getValue(this.store, s => s.keypadProgramming.trigger.events);
    console.log('keypad events are', button.events);
    if (button.events) {
      let interactions = [];
      button.events.forEach(event => {
        if (event.buttonName === button.label) {
          interactions.push(event);
        }
      });
      console.log('interactions for button are ', button, interactions);
      this.store.dispatch({ type: SET_EVENTS, payload: interactions });
    }
  }

  private groupByRoom(items: Array<any>) {
    let roomsWithKeypadsMap = {};
    items.forEach(light => {
      let room = roomsWithKeypadsMap[light.roomId];
      if (!room) {
        room = {
          name: light.roomName,
          id: light.roomId,
          devices: []
        };
        roomsWithKeypadsMap[light.roomId] = room;
      }
      room.devices.push(light);
    });

    let roomsWithDevices = [];
    Object.keys(roomsWithKeypadsMap).forEach(key => {
      roomsWithDevices.push(roomsWithKeypadsMap[key]);
    });

    this.store.dispatch({ type: SET_DEVICES, payload: roomsWithDevices });
  }

  private getEvents(keypad: Device) {
    this.broker.call({
      path: '/api/v1/items/' + keypad.id + '/events',
      queryString: {
        allevents: true
      }
    }).then(res => {
      // console.log('setting events', res);
      this.store.dispatch({ type: SET_EVENTS, payload: res });
      this.resolveButtons(res);
    }, err => {
      this.shared.error(err);
    });
  }

  private resolveButtons(events: Array<any>) {
    this.store.dispatch({ type: CLEAR_BUTTONS });

    let buttonMap = {};
    events.forEach(event => {
      if (event.buttonName) {
        let btn = buttonMap[event.buttonName];
        if (!btn) {
          btn = {
            label: event.buttonName,
            events: []
          };
          buttonMap[event.buttonName] = btn;
        }

        if (IGNORABLE_BUTTON_ACTIONS.indexOf(event.buttonAction.toLowerCase()) === -1) {
          btn.events.push(event);
        }
      }
    });

    let buttons = [];
    Object.keys(buttonMap).forEach(key => {
      buttons.push(buttonMap[key]);
    });

    console.log('setting buttons', buttons);
    this.store.dispatch({ type: SET_BUTTONS, payload: buttons });
    this.store.dispatch({ type: SET_READY, payload: true });
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/quickstarts/quickstart-keypads/services/keypads.service.ts